// Product card variant icons.
// - Mouse: hovering (or focusing) a colour icon shows that variant's photo in the card; moving
//   away puts the card's own photo back. Clicking opens that variant.
// - Touch: the first tap on an icon switches the card's photo to that colour and keeps it
//   (no page change); the card's photo/title links then open that colour. Tapping the
//   selected icon again opens it.
// Delegated, so it also works for cards added later by infinite scroll or filtering.

const DOT = '.variant-dots__dot[data-preview-src]';
let lastPointerType = 'mouse';

const isTouch = () => lastPointerType === 'touch' || window.matchMedia('(hover: none)').matches;

const cardFor = (el) => el.closest('.product-grid__item, product-card, .product-card');

/** The photo currently showing in the card (the slideshow may start on any slide). */
const cardImage = (dot) => {
  const card = cardFor(dot);
  if (!card) return null;
  return (
    card.querySelector('.card-gallery slideshow-slide[aria-hidden="false"] img') ||
    card.querySelector('.card-gallery slideshow-slide img, .card-gallery img')
  );
};

/** The card's own links to the product (photo, title, whole-card link), not the icons. */
const cardLinks = (dot) => {
  const card = cardFor(dot);
  if (!card) return [];
  return [...card.querySelectorAll('a[href]')].filter((a) => !a.closest('.variant-dots'));
};

const show = (dot, { sticky = false } = {}) => {
  const dots = dot.closest('.variant-dots');
  const img = (dots && dots.__img) || cardImage(dot);
  if (!img || !dots) return;
  dots.__img = img;
  if (!img.dataset.originalSrc) {
    img.dataset.originalSrc = img.getAttribute('src') || '';
    img.dataset.originalSrcset = img.getAttribute('srcset') || '';
  }
  const preload = new Image();
  preload.sizes = img.sizes;
  preload.srcset = dot.dataset.previewSrcset || '';
  preload.src = dot.dataset.previewSrc;
  const token = (img.dataset.previewToken = String(Math.random()));
  preload.decode().catch(() => {}).then(() => {
    if (img.dataset.previewToken !== token) return;
    img.srcset = dot.dataset.previewSrcset || '';
    img.src = dot.dataset.previewSrc;
  });
  dots.querySelectorAll('.is-active').forEach((el) => el.classList.remove('is-active'));
  dot.classList.add('is-active');

  if (sticky) {
    // Point the card at the chosen colour
    dots.__sticky = true;
    for (const link of cardLinks(dot)) {
      if (!link.dataset.originalHref) link.dataset.originalHref = link.getAttribute('href') || '';
      link.setAttribute('href', dot.getAttribute('href'));
    }
  }
};

const reset = (dots) => {
  if (dots.__sticky) return; // a colour chosen by tapping stays chosen
  const img = dots.__img;
  dots.querySelectorAll('.is-active').forEach((el) => el.classList.remove('is-active'));
  dots.__img = null;
  if (!img || !img.dataset.originalSrc) return;
  img.dataset.previewToken = '';
  img.srcset = img.dataset.originalSrcset;
  img.src = img.dataset.originalSrc;
  delete img.dataset.originalSrc;
  delete img.dataset.originalSrcset;
};

if (!window.__variantDots) {
  window.__variantDots = true;

  document.addEventListener(
    'pointerdown',
    (event) => {
      lastPointerType = event.pointerType || 'mouse';
    },
    true
  );

  const onEnter = (event) => {
    if (event.type === 'pointerover' && event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;
    if (event.type === 'focusin' && isTouch()) return;
    const dot = event.target instanceof Element ? event.target.closest(DOT) : null;
    if (dot) show(dot);
  };

  const onLeave = (event) => {
    if (event.type === 'pointerout' && event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;
    if (event.type === 'focusout' && isTouch()) return;
    const dots = event.target instanceof Element ? event.target.closest('.variant-dots') : null;
    if (!dots) return;
    if (event.relatedTarget instanceof Node && dots.contains(event.relatedTarget)) return;
    reset(dots);
  };

  // Touch: first tap selects the colour, a second tap on the same icon opens it
  document.addEventListener(
    'click',
    (event) => {
      const dot = event.target instanceof Element ? event.target.closest(DOT) : null;
      if (!dot || !isTouch()) return;
      const dots = dot.closest('.variant-dots');
      if (dot.classList.contains('is-active') && dots?.__sticky) return; // let it open
      event.preventDefault();
      event.stopPropagation();
      show(dot, { sticky: true });
    },
    true
  );

  document.addEventListener('pointerover', onEnter);
  document.addEventListener('focusin', onEnter);
  document.addEventListener('pointerout', onLeave);
  document.addEventListener('focusout', onLeave);
}